import { forwardRef, useId, type ButtonHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import { joinIds } from '../internal/aria';
import styles from './Switch.module.css';

export interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'children' | 'role'> {
  label: ReactNode;
  hint?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** State words beside the track. On by default — a toggle needs a state, and
   *  the track position alone is a shape-only signal. */
  showStateText?: boolean;
  labelPosition?: 'start' | 'end';
}

/**
 * `role="switch"` on a real `button`: Space and Enter, focus, and the disabled
 * state come from the platform. A checkbox styled as a track would announce the
 * wrong role, and a div would need every key rebuilt by hand.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  {
    label,
    hint,
    checked,
    onCheckedChange,
    showStateText = true,
    labelPosition = 'start',
    className,
    disabled,
    ...rest
  },
  ref,
) {
  const generated = useId();
  const labelId = `${generated}-label`;
  const hintId = `${generated}-hint`;

  return (
    <div
      className={clsx(styles.root, labelPosition === 'end' && styles.labelEnd, className)}
    >
      <span className={styles.text}>
        <span className={styles.label} id={labelId}>
          {label}
        </span>
        {hint !== undefined && (
          <span className={styles.hint} id={hintId}>
            {hint}
          </span>
        )}
      </span>

      <span className={styles.switchArea}>
        {showStateText && (
          <span className={styles.state} aria-hidden="true">
            {checked ? 'On' : 'Off'}
          </span>
        )}
        <button
          {...rest}
          ref={ref}
          type="button"
          role="switch"
          aria-checked={checked}
          aria-labelledby={joinIds(labelId, rest['aria-labelledby'])}
          aria-describedby={joinIds(hint !== undefined && hintId, rest['aria-describedby'])}
          disabled={disabled}
          className={styles.track}
          onClick={(event) => {
            rest.onClick?.(event);
            if (!event.defaultPrevented) onCheckedChange(!checked);
          }}
        >
          <span className={styles.thumb} aria-hidden="true" />
        </button>
      </span>
    </div>
  );
});
